import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CategoryIcon from "@mui/icons-material/Category";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import Party from "./Adv&Exp.Party";
import ExpenseHead from "./Adv&Exp.ExpenseHead";
import Expense from "./Adv&Exp.Expense";
import Advance from "./Adv&Exp.Advance";
import AdvanceSettlement from "./Adv&Exp.AdvanceSettlement";
import AllTypesFeesReport from "./Adv&Exp.AllTypesFeesReport";

function AdvExp() {
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("home");

  // 0 = SETUP
  // 1 = ACTIONS
  // 2 = REPORTS
  const [tabValue, setTabValue] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNavigate = (path) => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      navigate("/authentication/sign-in");
    } else {
      setActiveView(path);
    }
  };

  const handleBack = () => {
    setActiveView("home");
  };

  // Reusable menu button
  const MenuCard = ({ title, icon, path, color = "#2196F3" }) => (
    <Grid item xs={12} sm={6} md={4} lg={2.4}>
      <MDButton
        fullWidth
        variant="gradient"
        size="large"
        startIcon={icon}
        sx={{
          height: 90,
          fontSize: "0.85rem",
          fontWeight: 700,
          letterSpacing: "0.2px",
          whiteSpace: "normal",
          lineHeight: 1.2,
          borderRadius: "12px",
          color: "#FFFFFF !important",
          background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
          boxShadow: `0 4px 12px ${color}40`,
          transition: "all 0.25s ease",
          "&:hover": {
            background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
            transform: "translateY(-4px)",
            boxShadow: `0 8px 20px ${color}60`,
          },
          "& .material-icons-round, & .material-icons": {
            fontSize: "28px",
            color: "#FFFFFF !important",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "28px",
            color: "#FFFFFF",
          },
        }}
        onClick={() => handleNavigate(path)}
      >
        {title}
      </MDButton>
    </Grid>
  );

  MenuCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    path: PropTypes.string.isRequired,
    color: PropTypes.string,
  };

  const renderContent = () => {
    switch (activeView) {
      // =========================
      // SETUP
      // =========================
      case "party":
        return <Party />;

      case "expensehead":
        return <ExpenseHead />;

      // =========================
      // ACTIONS
      // =========================
      case "expense":
        return <Expense />;

      case "advance":
        return <Advance />;

      case "settlement":
        return <AdvanceSettlement />;

      // =========================
      // REPORTS
      // =========================
      case "alltypesfeesreport":
        return <AllTypesFeesReport />;

      // =========================
      // HOME
      // =========================
      default:
        return (
          <>
            {/* =========================
                TABS
            ========================= */}

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                TabIndicatorProps={{
                  sx: {
                    backgroundColor: ["#4CAF50", "#FF9800", "#9C27B0"][tabValue],
                    height: 5,
                    borderRadius: "4px 4px 0 0",
                  },
                }}
              >
                {[
                  {
                    label: "SETUP",
                    icon: <SettingsSuggestIcon />,
                    color: "#4CAF50",
                    bgColor: "rgba(76, 175, 80, 0.10)",
                  },
                  {
                    label: "ACTIONS",
                    icon: <TouchAppIcon />,
                    color: "#FF9800",
                    bgColor: "rgba(255, 152, 0, 0.12)",
                  },
                  {
                    label: "REPORTS",
                    icon: <AssessmentIcon />,
                    color: "#9C27B0",
                    bgColor: "rgba(156, 39, 176, 0.10)",
                  },
                ].map((item, index) => (
                  <Tab
                    key={item.label}
                    icon={item.icon}
                    iconPosition="top"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.2,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            letterSpacing: "0.1px",
                            lineHeight: 1.5,
                          }}
                        >
                          {item.label}
                        </Box>

                        {tabValue === index && (
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: 30,
                              color: item.color,
                              strokeWidth: 2,
                              filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.25))",
                              mt: 0.2,
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      minHeight: 105,
                      py: 1.2,
                      fontWeight: 800,
                      color: `${item.color} !important`,
                      opacity: 1,
                      borderRadius: "10px 10px 0 0",
                      transition: "all 0.25s ease",
                      "& .MuiSvgIcon-root": {
                        fontSize: 34,
                        color: item.color,
                        transition: "transform 0.25s ease",
                      },
                      "&:hover": {
                        backgroundColor: item.bgColor,
                        "& > .MuiTab-iconWrapper": {
                          transform: "scale(1.12)",
                        },
                      },
                      ...(tabValue === index && {
                        backgroundColor: item.bgColor,
                      }),
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* =========================
                SETUP
            ========================= */}
            {tabValue === 0 && (
              <Grid container spacing={2}>
                <MenuCard
                  title="PARTY MASTER"
                  icon={<CategoryIcon />}
                  path="party"
                  color="#4CAF50"
                />
                <MenuCard
                  title="EXPENSE HEAD"
                  icon={<CategoryIcon />}
                  path="expensehead"
                  color="#009688"
                />
              </Grid>
            )}

            {/* =========================
                ACTIONS
            ========================= */}
            {tabValue === 1 && (
              <Grid container spacing={2}>
                <MenuCard
                  title="EXPENSE"
                  icon={<ReceiptLongIcon />}
                  path="expense"
                  color="#FF9800"
                />
                <MenuCard
                  title="ADVANCE"
                  icon={<AccountBalanceWalletIcon />}
                  path="advance"
                  color="#F4511E"
                />
                <MenuCard
                  title="ADVANCE SETTLEMENT"
                  icon={<AccountBalanceWalletIcon />}
                  path="settlement"
                  color="#E65100"
                />
              </Grid>
            )}

            {/* =========================
                REPORTS
            ========================= */}
            {tabValue === 2 && (
              <Grid container spacing={2}>
                <MenuCard
                  title="ADVANCE & EXPENSE REPORT"
                  icon={<AssessmentIcon />}
                  path="alltypesfeesreport"
                  color="#9C27B0"
                />
              </Grid>
            )}
          </>
        );
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={2} pb={3}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                {/* HEADER - Matching Library Management System card design */}
                <MDBox
                  mx={2}
                  mt={-1}
                  py={1}
                  px={2}
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ color: "#FFFFFF" }}>account_balance_wallet</Icon>

                    <MDTypography variant="h6" color="white">
                      ADVANCES & EXPENSES
                    </MDTypography>
                  </MDBox>

                  {activeView !== "home" ? (
                    <MDButton
                      variant="contained"
                      onClick={handleBack}
                      startIcon={<Icon>arrow_back</Icon>}
                      sx={{
                        mb: 0,
                        color: "#FFFFFF",
                        backgroundColor: "rgba(255,255,255,0.18)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.28)",
                        },
                      }}
                    >
                      Back to Advances & Expenses
                    </MDButton>
                  ) : (
                    <MDButton
                      variant="contained"
                      onClick={() => navigate("/dashboard")}
                      startIcon={<Icon>dashboard</Icon>}
                      sx={{
                        mb: 0,
                        color: "#FFFFFF !important",
                        backgroundColor: "rgba(255,255,255,0.18) !important",
                        boxShadow: "none !important",
                        "& .material-icons": {
                          color: "#FFFFFF !important",
                        },
                        "&:hover": {
                          color: "#FFFFFF !important",
                          backgroundColor: "rgba(255,255,255,0.28) !important",
                          boxShadow: "none !important",
                        },
                        "&:focus": {
                          color: "#FFFFFF !important",
                          backgroundColor: "rgba(255,255,255,0.18) !important",
                        },
                        "&:active": {
                          backgroundColor: "rgba(255,255,255,0.28) !important",
                        },
                      }}
                    >
                      Dashboard
                    </MDButton>
                  )}
                </MDBox>

                {/* CONTENT */}
                <MDBox p={1}>{renderContent()}</MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>

      <MDBox display="flex" flexDirection="column" minHeight="50vh" />

      <Footer />
    </DashboardLayout>
  );
}

export default AdvExp;
